import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { Box, Divider } from "@material-ui/core";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { toast } from "sonner";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(2),
  },
  templateInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
  },
  templateName: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  templateBody: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 100,
  },
}));

const TemplateConfirmModal = ({ 
  modalOpen, 
  onClose, 
  templateId, 
  ticketId,
  whatsappId,
  onTemplateSent 
}) => {
  const classes = useStyles();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (modalOpen && templateId && whatsappId) {
      fetchTemplate();
    }
  }, [modalOpen, templateId, whatsappId]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates/list", {
        params: { whatsappId },
      });

      const foundTemplate = data.templates.find(t => t.id === templateId);
      if (foundTemplate) {
        setTemplate(foundTemplate);
      }
    } catch (err) {
      toastError(err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!templateId || !ticketId) {
      toast.error("Erro: Template ou ticket não encontrado");
      return;
    }

    setSending(true);
    try {
      await api.post("/templates/send", {
        ticketId,
        templateId
      });

      toast.success("Template enviado com sucesso!");
      
      if (onTemplateSent) {
        onTemplateSent();
      }
      
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const getTemplateBody = () => {
    if (!template || !template.components || template.components.length === 0) {
      return "Sem preview disponível";
    }

    const bodyComponent = template.components.find(comp => comp.type === "BODY");
    return bodyComponent?.text || "Sem preview disponível";
  };

  return (
    <Dialog open={modalOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Confirmar Envio de Template
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : template ? (
          <Box className={classes.templateInfo}>
            <Typography className={classes.templateName}>
              {template.name}
            </Typography>
            <Divider style={{ margin: "8px 0" }} />
            <Typography className={classes.templateBody}>
              {getTemplateBody()}
            </Typography>
          </Box>
        ) : (
          <Typography color="error">
            Template não encontrado
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={sending}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          onClick={handleConfirm}
          loading={sending}
          disabled={!template || sending}
          color="primary"
          variant="contained"
        >
          Enviar Template
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateConfirmModal;

